/**
 * qkey.co marketing chrome — every nav and footer destination, in one place.
 *
 * PORTED FROM QSAVE's src/lib/site/links.ts, same contract and same reasoning:
 * destinations are resolved here rather than scattered through the components,
 * so that a link audit reads one file and no `#` placeholder can reach
 * production by being missed in markup.
 *
 * THE CONTENT IS QKEY'S, THE STRUCTURE IS QSAVE'S. That is the whole point of
 * the port — same shape of nav (two menus plus flat links), same shape of
 * footer (a brand column plus link columns), different destinations.
 *
 * EVERY ROUTE BELOW WAS VERIFIED to exist as a <Route> in src/qkey/QkeyApp.tsx.
 * The page this replaces (src/qkey/pages/HomePage.tsx) shipped a footer of
 * eleven `href="#"` placeholders — Docs, API, SDKs, Status, About, Security,
 * Careers — that went nowhere. Those are NOT carried over. A destination
 * appears here only if it resolves; anything QKey wants but does not yet have
 * is absent rather than dead, and is listed in PENDING below so it is a
 * decision on the record rather than a broken link on the page.
 */

export interface SiteLink {
  label: string
  href: string
  external?: boolean
  /**
   * Single-path 24x24 brand glyph. Present ONLY on social links; a column whose
   * links carry one renders as an icon row rather than a text list — the data
   * decides, so there is no second flag to keep in sync.
   * `fill="currentColor"` at the render site, so the glyph inherits the link
   * colour and its hover transition for free.
   */
  icon?: string
}

/** A top-level nav entry: either a direct link, or a menu with children. */
export interface NavEntry {
  label: string
  /** Direct link. Mutually exclusive with `items`. */
  href?: string
  /** Dropdown children. Mutually exclusive with `href`. */
  items?: SiteLink[]
}

/**
 * DESTINATIONS QKEY DOES NOT YET HAVE.
 *
 * Recorded rather than invented. The outgoing footer linked all of these to
 * `#`, which reads as a working site until you click. When a real route or URL
 * exists, move the entry into FOOTER or NAV below — do not re-add a `#`.
 *
 *   Docs · API reference · SDKs · Status page · Security page · Careers
 *   QKey social accounts (X, LinkedIn, …) — QSAVE's accounts are QSAVE's and
 *   must not be borrowed here.
 *
 * SOCIAL is exported empty for exactly that reason. SiteFooter already renders
 * an icon row for any column whose links carry an `icon`, so supplying real
 * QKey account URLs here is the only change needed to light that column up.
 */
export const SOCIAL: SiteLink[] = []

/**
 * Primary nav.
 *
 * Structure mirrors QSAVE's: two dropdown menus carrying the grouped material,
 * flat links for the singular pages, five top-level entries. The grouping is
 * QKey's own product line as defined in QKEY_BRAND_GUIDELINES.md §1 — Quantum
 * Vault, PQP, PQ Exchange, USPQ, PQ Chain, Enterprise migration.
 *
 * The DEVELOPER tools that the app nav gates behind `isLocalhost()`
 * (src/components/layout/Navigation.tsx) are deliberately NOT here. That gate
 * exists because internal tooling never ships on a public host, and the
 * marketing chrome is the most public surface there is.
 */
export const NAV: NavEntry[] = [
  {
    label: 'Products',
    items: [
      { label: 'Quantum Vault — HD wallet', href: '/hd-wallet' },
      { label: 'PQP — Post-Quantum Pay', href: '/pqp' },
      { label: 'PQ Exchange', href: '/pq-exchange' },
      { label: 'USPQ — post-quantum dollar', href: '/uspq' },
      { label: 'PQ Chain — backup fork', href: '/pq-chain' },
      { label: 'Enterprise migration', href: '/enterprise' },
    ],
  },
  { label: 'Quantum Keys', href: '/quantum-keys' },
  { label: 'Standards Mark', href: '/standards-mark' },
  {
    label: 'Resources',
    items: [
      { label: 'How protection works', href: '/protection-workflow' },
      { label: 'Certification programme', href: '/certification' },
      { label: 'Bitcoin developer tools', href: '/bitcoin-tools' },
      { label: 'Brand guidelines', href: '/brand-guidelines' },
      { label: 'FAQ', href: '/faq' },
      { label: 'About', href: '/about' },
      { label: 'QSAVE Protocol ↗', href: 'https://qsave.org', external: true },
    ],
  },
  { label: 'Partners', href: '/partners' },
]

/** The nav's accent-ground CTA — QSAVE's "Join" slot. */
export const CTA: SiteLink = { label: 'Book a demo', href: '/contact' }

/** The quiet link beside it — QSAVE's "Sign in" slot. */
export const SECONDARY_CTA: SiteLink = { label: 'Open the wallet', href: '/hd-wallet' }

export interface FooterColumn {
  heading: string
  links: SiteLink[]
}

/**
 * Footer columns. Five columns beside the brand blurb, matching the QSAVE
 * grid; the sixth track carries the social row and stays empty until real QKey
 * accounts exist (see SOCIAL above).
 */
export const FOOTER: FooterColumn[] = [
  {
    heading: 'PRODUCTS',
    links: [
      { label: 'Quantum Vault', href: '/hd-wallet' },
      { label: 'PQP', href: '/pqp' },
      { label: 'PQ Exchange', href: '/pq-exchange' },
      { label: 'USPQ', href: '/uspq' },
      { label: 'PQ Chain', href: '/pq-chain' },
    ],
  },
  {
    heading: 'BUILD',
    links: [
      { label: 'Quantum Keys', href: '/quantum-keys' },
      { label: 'Bitcoin tools', href: '/bitcoin-tools' },
      { label: 'Transaction builder', href: '/transaction' },
      { label: 'Multi-wallet dashboard', href: '/wallets' },
    ],
  },
  {
    heading: 'ASSURANCE',
    links: [
      { label: 'Standards Mark', href: '/standards-mark' },
      { label: 'Certification', href: '/certification' },
      { label: 'How protection works', href: '/protection-workflow' },
      { label: 'Brand guidelines', href: '/brand-guidelines' },
    ],
  },
  {
    heading: 'COMPANY',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Enterprise', href: '/enterprise' },
      { label: 'Partners', href: '/partners' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Contact', href: '/contact' },
    ],
  },
  {
    heading: 'LEGAL',
    links: [
      { label: 'Terms', href: '/terms' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'QSAVE Protocol ↗', href: 'https://qsave.org', external: true },
    ],
  },
]
